import { z } from 'zod';

export const HealthRecommendationCategorySchema = z.enum([
  'test-coverage',
  'bus-factor',
  'collisions',
  'stale-issues',
  'refactor',
  'dead-code',
  'codeowners',
]);

export const HealthRecommendationFocusSchema = z.union([
  HealthRecommendationCategorySchema,
  z.literal('mixed'),
]);

const LocationSchema = z.object({
  nodeId: z.string(),
  path: z.string().optional(),
  startLine: z.number().int().positive().optional(),
  endLine: z.number().int().positive().optional(),
});

const EvidenceSchema = z.object({
  code: z.string(),
  value: z.union([z.string(), z.number(), z.boolean()]),
  location: LocationSchema.optional(),
  relatedNodeIds: z.array(z.string()).optional(),
});

const RankReasonSchema = z.object({
  code: z.string(),
  value: z.union([z.string(), z.number()]),
});

const ReadOnlyActionSchema = z.object({ readOnly: z.literal(true) });

export const HealthRecommendationActionSchema = z.discriminatedUnion('kind', [
  ReadOnlyActionSchema.extend({
    kind: z.literal('add-test-coverage'),
    targetPath: z.string(),
  }),
  ReadOnlyActionSchema.extend({
    kind: z.literal('propose-codeowners-entry'),
    directory: z.string(),
    owners: z.array(z.string()),
  }),
  ReadOnlyActionSchema.extend({
    kind: z.literal('inspect-collision'),
    file: z.string(),
    issueIds: z.array(z.string()),
    branches: z.array(z.string()),
  }),
  ReadOnlyActionSchema.extend({
    kind: z.literal('suggest-issue-files'),
    issueId: z.string(),
  }),
  ReadOnlyActionSchema.extend({
    kind: z.literal('inspect-refactor-boundary'),
    nodeId: z.string(),
    hops: z.literal(2),
    communityIds: z.tuple([z.number().int(), z.number().int()]),
  }),
  ReadOnlyActionSchema.extend({
    kind: z.literal('verify-deletion'),
    targetPath: z.string(),
    unverified: z.literal(true),
  }),
  ReadOnlyActionSchema.extend({
    kind: z.literal('verify-hygiene-cleanup'),
    targetPath: z.string(),
    line: z.number().int().positive(),
    verificationRequired: z.literal(true),
  }),
]);

const FreshnessSchema = z.object({
  repo: z.string(),
  headSha: z.string(),
  scanId: z.string(),
  scannedAt: z.iso.datetime(),
  ageDays: z.number().nonnegative(),
  staleAfterDays: z.number().positive(),
});

export const HealthRecommendationItemSchema = z.object({
  id: z.string(),
  source: z.enum([
    'layered-graph',
    'simplification',
    'coverage-gaps',
    'hygiene',
  ]),
  category: HealthRecommendationCategorySchema,
  severity: z.enum(['critical', 'high', 'medium', 'low']),
  target: LocationSchema,
  evidence: z.array(EvidenceSchema),
  rankReasons: z.array(RankReasonSchema),
  action: HealthRecommendationActionSchema,
  scan: FreshnessSchema,
});

const CategoryGapSchema = z.enum([
  'missing-code-layer',
  'sparse-code-layer',
  'missing-people-layer',
  'sparse-people-layer',
  'missing-ownership-edges',
  'missing-issue-layer',
  'sparse-issue-layer',
  'missing-hunk-evidence',
]);

const CategoryAvailabilitySchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('loaded') }),
  z.object({ kind: z.literal('not-measured'), reason: CategoryGapSchema }),
]);

const CategoriesSchema = z.record(
  HealthRecommendationCategorySchema,
  CategoryAvailabilitySchema,
);

const SourceSchema = z.union([
  z.object({ id: z.literal('layered-graph'), kind: z.literal('loaded') }),
  z.object({
    id: z.literal('coverage-gaps'),
    kind: z.literal('loaded'),
    fetchedAt: z.iso.datetime(),
    headSha: z.string().min(1),
    fileCount: z.number().int().nonnegative(),
    truncated: z.boolean(),
  }),
  z.object({
    id: z.literal('coverage-gaps'),
    kind: z.literal('partial'),
    reason: z.enum([
      'codecov-not-integrated',
      'codecov-not-connected',
      'codecov-repo-mismatch',
      'codecov-report-pending',
      'codecov-unavailable',
    ]),
  }),
  z.object({
    id: z.literal('coverage-gaps'),
    kind: z.literal('not-measured'),
    reason: z.enum(['no-scan', 'coverage-not-measured']),
  }),
  z.object({
    id: z.literal('simplification'),
    kind: z.literal('loaded'),
  }),
  z.object({
    id: z.literal('simplification'),
    kind: z.literal('partial'),
    reason: z.literal('static-analysis-not-run'),
  }),
  z.object({
    id: z.literal('simplification'),
    kind: z.literal('not-measured'),
    reason: z.enum(['no-scan', 'missing-code-layer', 'sparse-code-layer']),
  }),
  z.object({ id: z.literal('hygiene'), kind: z.literal('loaded') }),
  z.object({
    id: z.literal('hygiene'),
    kind: z.literal('not-measured'),
    reason: z.enum(['no-scan', 'hygiene-evidence-not-captured']),
  }),
  z.object({
    id: z.literal('layered-graph'),
    kind: z.literal('not-measured'),
    reason: z.literal('no-scan'),
  }),
  z.object({
    id: z.literal('pr-scan-join'),
    kind: z.literal('not-measured'),
    reason: z.literal('source-not-implemented'),
  }),
]);

const MeasuredAvailabilitySchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('loaded'), categories: CategoriesSchema }),
  z.object({ kind: z.literal('partial'), categories: CategoriesSchema }),
  z.object({ kind: z.literal('stale'), categories: CategoriesSchema }),
]);

export const HealthRecommendationCollectionSchema = z.union([
  z.object({
    availability: z.object({ kind: z.literal('no-scan') }),
    scan: z.null(),
    sources: z.array(SourceSchema),
    items: z.tuple([]),
  }),
  z.object({
    availability: MeasuredAvailabilitySchema,
    scan: FreshnessSchema,
    sources: z.array(SourceSchema),
    items: z.array(HealthRecommendationItemSchema).max(10),
  }),
]);

export type HealthRecommendationItem = z.infer<
  typeof HealthRecommendationItemSchema
>;
export type HealthRecommendationCollection = z.infer<
  typeof HealthRecommendationCollectionSchema
>;
export type HealthRecommendationFocus = z.infer<
  typeof HealthRecommendationFocusSchema
>;
